import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import {
  Cookie,
  FileText,
  HelpCircle,
  LogOut,
  Puzzle,
  Settings,
  Shield,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import { AppUser } from '../types';
import { useBalance } from '../lib/useBalance';
import { unitsToTokens } from '../lib/tokens/costs';
import { useConsent } from '../consent/ConsentContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface UserMenuProps {
  user: AppUser;
  onLogout: () => void;
}

export function UserMenu({ user, onLogout }: UserMenuProps) {
  const { t, i18n } = useTranslation();
  const { openModal: openConsentModal } = useConsent();
  const [, navigate] = useLocation();
  const { balance, refresh: refreshBalance } = useBalance();

  return (
    <DropdownMenu onOpenChange={(open) => { if (open) refreshBalance(); }}>
      <DropdownMenuTrigger
        className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 coarse:inline-flex coarse:items-center coarse:justify-center coarse:min-h-11 coarse:min-w-11"
        aria-label={user.name || user.email}
        title={user.name || user.email}
      >
        <Avatar className="h-8 w-8 cursor-pointer">
          <AvatarImage src={user.avatarUrl || ''} />
          <AvatarFallback>
            {user.name?.charAt(0) || user.email.charAt(0)}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5 border-b border-zinc-100 dark:border-zinc-800 mb-1">
          <p className="text-sm font-medium truncate">{user.name || user.email}</p>
          <p className="text-xs text-zinc-500 truncate">{user.email}</p>
        </div>
        {user.role === 'admin' && (
          <DropdownMenuItem
            onClick={() => navigate('/admin')}
            className="text-violet-600 dark:text-violet-400"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            {t('admin.menuLink')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate('/settings/balance')}>
          <Settings className="w-4 h-4 mr-2" />
          <span className="flex-1">{t('account.settings')}</span>
          {balance && (
            <span className="ml-auto text-xs text-zinc-500 tabular-nums inline-flex items-center gap-1">
              <Wallet className="w-3 h-3" />
              {unitsToTokens(Math.max(0, balance.balance - balance.reserved)).toLocaleString(i18n.language)}
            </span>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/privacy')}>
          <Shield className="w-4 h-4 mr-2" />
          {t('cookies.footer.privacy')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/terms')}>
          <FileText className="w-4 h-4 mr-2" />
          {t('cookies.footer.terms')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/download')}>
          <Puzzle className="w-4 h-4 mr-2" />
          {t('cookies.footer.download')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/docs')}>
          <HelpCircle className="w-4 h-4 mr-2" />
          {t('account.helpShortcuts')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openConsentModal}>
          <Cookie className="w-4 h-4 mr-2" />
          {t('cookies.footer.managePreferences')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onLogout} className="text-red-600">
          <LogOut className="w-4 h-4 mr-2" />
          {t('auth.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
